import dynamic from "next/dynamic"
import React from "react"
import classNames from "classnames"
import { useLayout, useTranslations } from "@dustin-web/microsite-components/src/hooks"
import { ListingType, type LandingPageGroupList } from "../data/types"
import { getDigitalDataOrigin } from "../utils/digital-data"
import { getColorThemeClass } from "../utils/color-theme"
import LandingPageVariantMultiSlider from "./variants/multi-slider"

const LandingPageVariantAside = dynamic(() => import('./variants/aside'))
const LandingPageVariantSlider = dynamic(() => import('./variants/slider'))
const LandingPageVariantSticky = dynamic(() => import('./variants/sticky'))

type Props = {
  groupLists: LandingPageGroupList[]
}

const LandingPageMultiList = ({ groupLists }: Props) => {
  const { responsive } = useLayout()
  const { translate } = useTranslations()

  const asideList = groupLists.find(list => list.attributes.productListingType === ListingType.Aside)

  if (asideList) {
    if (groupLists.length > 2) {
      // eslint-disable-next-line no-console
      console.warn('Landing page aside-list only supports 2 lists in a group')
    }

    const slideList = groupLists.find(list => list.attributes.productListingType === ListingType.Slider)

    const {
      displayName: asideDisplayName = '',
      colorTheme: asideColorTheme = 'gray',
      stickyOnLarge: asideSticky
    } = asideList.attributes ?? {}

    const {
      displayName: slideDisplayName = '',
      colorTheme: slideColorTheme = 'gray'
    } = slideList?.attributes ?? {}

    const fullWidth = responsive.breakpoints.max.LG || (responsive.windowSize.width > 1600)
    const sticky = asideSticky && responsive.windowSize.width > 1600

    return (
      <>
        <div className={classNames('col-sm-12 col-12', {
          'col-lg-8': !sticky
        })}>
          <h2 className={`u-mb-16 h2 d-flex ${getColorThemeClass(slideColorTheme)}`}>
            <span className={'u-mr-8'}>{translate(slideDisplayName)}</span>
          </h2>
          <LandingPageVariantSlider
            groupList={slideList!}
            digitalDataOrigin={getDigitalDataOrigin(slideDisplayName)}
            fullWidth={fullWidth}
            sliderType={sticky || fullWidth ? 'startpage' : 'startpageFullWidthFalse'} />
        </div>
        {!sticky &&
          <>
            <div className="col-sm-12 col-12 col-lg-4">
              <h2 className={`u-mb-16 h2 d-flex ${getColorThemeClass(asideColorTheme)}`}>
                <span className={'u-mr-8'}>{translate(asideDisplayName)}</span>
              </h2>
              <LandingPageVariantAside
                groupList={asideList}
                digitalDataOrigin={getDigitalDataOrigin(asideDisplayName)} />
            </div>
          </>
        }
        {sticky &&
          <LandingPageVariantSticky
            groupList={asideList}
            digitalDataOrigin={getDigitalDataOrigin(asideDisplayName)} />
        }
      </>
    )
  }

  return <LandingPageVariantMultiSlider groupLists={groupLists} />
}

export default LandingPageMultiList
